import React, { useEffect, useLayoutEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

export type GenerateChildProgressStatus = 'running' | 'success' | 'error';

interface GenerateChildProgressModalProps {
    isOpen: boolean;
    status: GenerateChildProgressStatus;
    lines: string[];
    contextTitle?: string;
    errorMessage?: string | null;
    onClose: () => void;
}

const GenerateChildProgressModal: React.FC<GenerateChildProgressModalProps> = ({
    isOpen,
    status,
    lines,
    contextTitle,
    errorMessage,
    onClose
}) => {
    const focusRef = useRef<HTMLDivElement>(null);
    const logEndRef = useRef<HTMLDivElement>(null);

    useLayoutEffect(() => {
        if (!isOpen) return;
        const id = requestAnimationFrame(() => {
            focusRef.current?.focus();
        });
        return () => cancelAnimationFrame(id);
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen) return;
        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape' && status !== 'running') {
                e.preventDefault();
                onClose();
            }
        };
        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [isOpen, status, onClose]);

    useEffect(() => {
        if (!isOpen) return;
        logEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }, [isOpen, lines]);

    if (!isOpen) return null;

    const canDismiss = status !== 'running';

    return createPortal(
        <div
            className="fixed inset-0 z-[10000] flex items-center justify-center p-4 pointer-events-auto"
            role="presentation"
        >
            <div
                className="fixed inset-0 bg-black/60 backdrop-blur-sm"
                aria-hidden
                onClick={canDismiss ? onClose : undefined}
                style={{ cursor: canDismiss ? 'pointer' : 'default' }}
            />
            <div
                ref={focusRef}
                tabIndex={-1}
                role="dialog"
                aria-modal="true"
                aria-labelledby="generate-child-progress-title"
                className="relative z-[10001] w-full max-w-2xl max-h-[min(85vh,720px)] flex flex-col bg-slate-900/95 border border-white/20 rounded-2xl shadow-2xl outline-none focus:ring-2 focus:ring-purple-500/60"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-center justify-between px-6 py-4 border-b border-white/10 shrink-0">
                    <h2 id="generate-child-progress-title" className="text-lg font-semibold text-white pr-4">
                        Generating child requirements
                        {contextTitle ? (
                            <span className="block text-sm font-normal text-white/70 mt-1 truncate">
                                {contextTitle}
                            </span>
                        ) : null}
                    </h2>
                    {canDismiss && (
                        <button
                            type="button"
                            onClick={onClose}
                            className="text-gray-400 hover:text-white p-2 rounded-lg hover:bg-white/10 shrink-0"
                            aria-label="Close"
                        >
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    )}
                </div>

                <div className="flex-1 min-h-0 px-6 py-4 flex flex-col gap-3">
                    {status === 'running' && (
                        <p className="text-sm text-purple-200/90 flex items-center gap-2 shrink-0">
                            <span
                                className="inline-block h-2 w-2 rounded-full bg-purple-400 animate-pulse"
                                aria-hidden
                            />
                            Working… please keep this window open.
                        </p>
                    )}
                    {status === 'error' && errorMessage && (
                        <p className="text-sm text-red-300 shrink-0" role="alert">
                            {errorMessage}
                        </p>
                    )}
                    <div
                        className="flex-1 min-h-[200px] overflow-y-auto rounded-lg bg-black/40 border border-white/10 p-3 font-mono text-sm text-gray-200 whitespace-pre-wrap break-words"
                        aria-live="polite"
                        aria-relevant="additions"
                    >
                        {lines.length === 0 ? (
                            <span className="text-white/50">Starting…</span>
                        ) : (
                            lines.map((line, i) => (
                                <div key={i} className="mb-1 last:mb-0">
                                    {line}
                                </div>
                            ))
                        )}
                        <div ref={logEndRef} />
                    </div>
                </div>

                <div className="px-6 py-4 border-t border-white/10 flex justify-end shrink-0">
                    {canDismiss ? (
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 rounded-lg bg-gradient-to-r from-purple-500 to-blue-600 hover:from-purple-600 hover:to-blue-700 text-white font-medium"
                        >
                            {status === 'success' ? 'Done' : 'Close'}
                        </button>
                    ) : (
                        <span className="text-sm text-white/50">Please wait…</span>
                    )}
                </div>
            </div>
        </div>,
        document.body
    );
};

export default GenerateChildProgressModal;
